/**
 * Session materialization for Artists browse — index + variant grouping keyed by
 * catalog revision. Survives tab switches without rescanning the full corpus.
 */

import { getArtistBrowseIndex } from './artistBrowseIndex';
import {
  getGroupedArtistsCached,
  getArtistVariantLookupCached,
} from './artistGrouping';

let materializedKey = '';
/** @type {{ rawIndex: Array<object>, grouped: Array<object>, variantLookup: Map<string, Set<string>> } | null} */
let materializedSnapshot = null;

function buildMaterializedKey(catalogRevision, albumCount) {
  return `${String(catalogRevision ?? '')}:${albumCount ?? 0}`;
}

/**
 * @param {{ record?: (phase: string, ms: number) => void } | null | undefined} phaseRecorder
 */
export function getMaterializedArtistBrowse(displayAlbums, catalogRevision, phaseRecorder = null) {
  const albumCount = displayAlbums?.length ?? 0;
  const key = buildMaterializedKey(catalogRevision, albumCount);
  if (materializedSnapshot && materializedKey === key) {
    phaseRecorder?.record?.('index_build', 0);
    phaseRecorder?.record?.('variant_grouping', 0);
    return materializedSnapshot;
  }

  const indexStart = typeof performance !== 'undefined' ? performance.now() : 0;
  const rawIndex = getArtistBrowseIndex(displayAlbums, catalogRevision);
  if (phaseRecorder?.record) {
    phaseRecorder.record('index_build', performance.now() - indexStart);
  }

  const groupingStart = typeof performance !== 'undefined' ? performance.now() : 0;
  const grouped = getGroupedArtistsCached(rawIndex, catalogRevision);
  const variantLookup = getArtistVariantLookupCached(rawIndex, catalogRevision);
  if (phaseRecorder?.record) {
    phaseRecorder.record('variant_grouping', performance.now() - groupingStart);
  }

  materializedKey = key;
  materializedSnapshot = { rawIndex, grouped, variantLookup };
  return materializedSnapshot;
}

export function isMaterializedArtistBrowseWarm(catalogRevision, albumCount) {
  return materializedKey === buildMaterializedKey(catalogRevision, albumCount) && materializedSnapshot != null;
}

/** @internal test helper */
export function resetArtistBrowseMaterializationForTests() {
  materializedKey = '';
  materializedSnapshot = null;
}
